/**
 * CR-4865: Group POChangeLog entries by change_request_ref into the
 * Delivery-Appeal-style structure (submission, decision, history rows).
 */
import { useEffect, useState } from 'react';
import { ChangelogEntry } from '../common/Changelog';
import { POChangeRequestAttachment, purchasingAPI } from '../../lib/api';
import type { ChangeRequestGroup, ChangeRequestStatus } from './ChangeRequestCard';
import { hasOpenChangeRequest, type ProposalFields } from './pendingProposal';
import { CRD_FIELD_NAMES, isDecisionRow, isProposalRow, markedOutcome } from './changelogFields';
import { changeRequestEntries } from './changeRequestsTabHelpers';
import { CUSTOMER_REQUIRED_DATE_SHORT, SUPPLIER_PROMISE_DATE_SHORT } from '../../config/dateLabels';

// CR-7166: Added the Customer Required Date mapping for customer-raised date
// changes. Customer changes their required date (customer_required_date;
// rows before 15 Sep 2026 say delivery_date), supplier changes their promise
// date (supplier_promise_date).
const FIELD_LABEL: Record<string, string> = {
  supplier_promise_date: SUPPLIER_PROMISE_DATE_SHORT,
  customer_required_date: CUSTOMER_REQUIRED_DATE_SHORT,
  delivery_date: CUSTOMER_REQUIRED_DATE_SHORT,
  quantity: 'Quantity',
  unit_price: 'Unit Price',
};

const FIELD_TO_CHANGE_TYPE: Record<string, ChangeRequestGroup['changeType']> = {
  supplier_promise_date: 'promise_date',
  customer_required_date: 'required_date',
  delivery_date: 'required_date',
  quantity: 'quantity',
  unit_price: 'unit_price',
};

function deriveStatus(entry: ChangelogEntry | null): ChangeRequestStatus | null {
  if (!entry) return null;
  // CR-8590: the decision's own marker first; the user's comment after it
  // ("original quantity was accepted") must not decide the status.
  const marked = markedOutcome(entry);
  if (marked !== undefined) return marked;
  const reason = (entry.reason || '').toLowerCase();
  if (entry.field_changed === 'acknowledge_status') {
    if (entry.new_value === 'change_declined') return 'declined';
    if (entry.new_value === 'accepted') {
      if (/cancelled/.test(reason)) return 'cancelled';
      return 'accepted';
    }
    if (/cancelled/.test(reason)) return 'cancelled';
  }
  // Accept rows written on the field itself (supplier_promise_date etc.) with
  // "accepted" in the reason.
  if (/\b(accepted)\b/.test(reason)) return 'accepted';
  if (/\b(rejected|declined)\b/.test(reason)) return 'declined';
  if (/\bcancelled\b/.test(reason)) return 'cancelled';
  return null;
}

/**
 * Group changelog entries by `change_request_ref`. Only entries with a ref
 * participate — rows without a ref (legacy data) are returned in `orphans`
 * so the caller can render them via the legacy unordered list.
 */
export function groupChangeRequests(entries: ChangelogEntry[]): {
  groups: ChangeRequestGroup[];
  orphans: ChangelogEntry[];
} {
  const byRef = new Map<number, ChangelogEntry[]>();
  const orphans: ChangelogEntry[] = [];
  entries.forEach(entry => {
    const ref = entry.change_request_ref ?? null;
    if (ref == null) {
      orphans.push(entry);
      return;
    }
    const bucket = byRef.get(ref) || [];
    bucket.push(entry);
    byRef.set(ref, bucket);
  });

  const groups: ChangeRequestGroup[] = [];
  byRef.forEach((rows, ref) => {
    // Oldest-first ordering so the submission row (the proposal) is first
    // and any decision row comes later.
    const ordered = [...rows].sort(
      (a, b) => new Date(a.change_date).getTime() - new Date(b.change_date).getTime()
    );
    // CR-7166: Also recognize the Customer Required Date (either spelling)
    // for customer-raised date changes.
    const isChangeField = (r: ChangelogEntry) => (
      r.field_changed === 'supplier_promise_date' ||
      CRD_FIELD_NAMES.includes(r.field_changed) ||
      r.field_changed === 'quantity' ||
      r.field_changed === 'unit_price'
    );
    // The submission is the proposal row (CR-8596: known by its "Supplier
    // proposed …" / "Customer proposed …" prefix, whatever words follow —
    // "will be rejected" in the supplier's own reason used to make this skip
    // the proposal and read it as the decision). Rows written before the
    // prefix existed fall back to the first value-change row that does not
    // read like a decision; those decision rows can sit on the field itself
    // (accept_quantity_change / accept_unit_price_change / round-1's
    // accept_delivery_date).
    const submission = ordered.find(r => isProposalRow(r) && isChangeField(r))
      || ordered.find(r => !isDecisionRow(r) && isChangeField(r))
      || ordered[0];
    // CR-4731 round 2: decision rows are not always on acknowledge_status.
    // accept_quantity_change / accept_unit_price_change have always written
    // the decision on the field itself ('quantity' / 'unit_price'), and
    // CR-4731 round 1 made accept_delivery_date do the same on
    // 'supplier_promise_date'. Pick the latest row whose reason marks it as a
    // resolution; fall back to the legacy acknowledge_status row.
    // CR-863 (Aaron, 16 Oct 2026): a proposal row ("Supplier proposed …" /
    // "Customer proposed …") is never the decision, so the side's own reason
    // ("quote rejected by mill") cannot close its request. Same rule as the Cost
    // tab's price change requests (backend purchasing/price_change_requests.py).
    // CR-8590: and a decision is known by its own marker, so the reviewer's
    // comment cannot turn one outcome into another (isDecisionRow).
    const decision =
      [...ordered].reverse().find(isDecisionRow) ||
      ordered.find(r => r.field_changed === 'acknowledge_status') ||
      null;

    const derivedStatus = deriveStatus(decision);
    const status: ChangeRequestStatus = derivedStatus ?? 'open';

    const changeType: ChangeRequestGroup['changeType'] =
      FIELD_TO_CHANGE_TYPE[submission?.field_changed || ''] || 'other';
    const fieldLabel = FIELD_LABEL[submission?.field_changed || ''] || submission?.field_changed || 'Change';

    // Extract po_line id from any entry that carries it.
    const poLineId =
      (ordered.find(r => (r as unknown as { po_line?: number }).po_line)
        ?.[
          'po_line' as unknown as keyof ChangelogEntry
        ] as unknown as number) || 0;

    groups.push({
      ref,
      poLineId,
      status,
      changeType,
      fieldLabel,
      oldValue: submission?.old_value || '',
      newValue: submission?.new_value || '',
      submission: submission || null,
      decision,
      history: ordered,
    });
  });

  // Sort groups by ref DESC — newest requests at the top.
  groups.sort((a, b) => b.ref - a.ref);
  return { groups, orphans };
}

/** The value a proposed field and a request card are compared on. */
const sameValue = (field: 'date' | 'number', a: string | number, b: string) => (
  field === 'date' ? String(a).slice(0, 10) === b.slice(0, 10) : Number(a) === Number(b)
);

/**
 * CR-6409 rework: the line's open proposals that no request card lists.
 *
 * The Active Change Request box shows what the line has proposed now. A
 * proposal normally also has a card ("Open"), but QA found one with no card
 * (PRO00212 line 3: an open Customer Required Date change above a list holding
 * only an older promise-date request), so the label read one request short.
 * Each proposed field is its own request (propose_changes), and it is already
 * listed when an open card of its kind proposes the same value.
 */
function unlistedOpenProposals(line: ProposalFields, groups: ChangeRequestGroup[]): number {
  if (!hasOpenChangeRequest(line)) return 0;
  const legs: Array<[Array<ChangeRequestGroup['changeType']>, 'date' | 'number', string | number | null | undefined]> = [
    [['promise_date', 'required_date'], 'date', line.supplier_proposed_date],
    [['quantity'], 'number', line.supplier_proposed_qty],
    [['unit_price'], 'number', line.supplier_proposed_unit_price],
  ];
  return legs.filter(([types, kind, proposed]) => (
    proposed != null && proposed !== ''
    && !groups.some((g) => g.status === 'open' && types.includes(g.changeType)
      && sameValue(kind, proposed, g.newValue))
  )).length;
}

/**
 * CR-6409: the number on the PO Line pop-up's Change Requests tab.
 *
 * One per change request the tab shows: the cards under its own "Change
 * Requests (n)" heading, plus an open proposal in the Active Change Request
 * box that no card lists (CR-6409 rework). Legacy rows written before
 * requests carried a ref are history, not requests, and are not counted.
 *
 * @param changelogs - Every change-log row on the line.
 * @param line - The line, for its open proposal; omitted, only cards count.
 * @returns How many change requests the tab shows.
 */
export function countChangeRequests(changelogs: ChangelogEntry[], line?: ProposalFields | null): number {
  const { groups } = groupChangeRequests(changeRequestEntries(changelogs));
  return groups.length + (line ? unlistedOpenProposals(line, groups) : 0);
}

/**
 * Load all change-request attachments for a PO line and expose a
 * refresh handle. Indexed by change_request_ref for easy lookup per card.
 */
export function useChangeRequestAttachments(poLineId: number | null): {
  attachmentsByRef: Map<number, POChangeRequestAttachment[]>;
  loading: boolean;
  refresh: () => void;
} {
  const [attachments, setAttachments] = useState<POChangeRequestAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!poLineId) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    purchasingAPI
      .getChangeRequestAttachments({ po_line: poLineId })
      .then(res => {
        if (cancelled) return;
        setAttachments(res.data.results || []);
      })
      .catch(() => {
        if (cancelled) return;
        setAttachments([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [poLineId, reloadTick]);

  const attachmentsByRef = new Map<number, POChangeRequestAttachment[]>();
  attachments.forEach(att => {
    const bucket = attachmentsByRef.get(att.change_request_ref) || [];
    bucket.push(att);
    attachmentsByRef.set(att.change_request_ref, bucket);
  });

  return {
    attachmentsByRef,
    loading,
    refresh: () => setReloadTick(t => t + 1),
  };
}
